import { hardCodedAutoPlacedPropLabels } from "building-types/entities/labelTypes";
import type { RoofGenerationStageInput, RoofGenerationStageResult } from "building-types/roofs/roofGenerationTypes";
import { EaveBracketPlacement } from "building-types/settings/roofBaseTypes";
import { EaveCutType, PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { PropViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { radianToDegree } from "common/utils/math";
import uid from "common/utils/uid";
import { getPropSrc } from "doc/elements/propCatalog/propCatalogHelpers";
import { mergedPropLibraryNew, propNew } from "doc/propTools";
import { defaultPropViewNotes } from "doc/settings/defaultViewNoteSettings";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { aabb3XLength } from "geom/aabb3";
import { polyLine2Copy, polyLine2EdgeNormal, polyLine2ReverseCopy } from "geom/polyLine2";
import { polyLine3EdgeLength, polyLine3EvalEdge, polyLine3New } from "geom/polyLine3";
import { vec2AngleBetween, vec2Rotate } from "geom/vec2";

export async function generateRoofEaveBrackets({
  subRoof,
  subRoofOptions,
}: RoofGenerationStageInput): Promise<RoofGenerationStageResult> {
  const { roofInputSettings } = subRoofOptions;
  const { roofVolume, props } = subRoof;
  const { eaveEdges } = roofVolume;
  const {} = subRoofOptions;
  const { propCatalog, assets } = roofInputSettings;
  const mergedPropLibrary = mergedPropLibraryNew(assets);

  for (const eaveEdge of eaveEdges) {
    const { roofMaterialTrimAndProps } = eaveEdge;
    assertIsNotNullOrUndefined(roofMaterialTrimAndProps);

    const { includeEaveBrackets } = roofMaterialTrimAndProps;

    if (includeEaveBrackets) {
      const {
        eaveBracketParams = [0.5],
        eaveBracketParamsNormalized = true,
        eaveBracketPlacement = EaveBracketPlacement.START_TO_END,
        eaveBracketPropId,
        fasciaSize = 0,
      } = roofMaterialTrimAndProps;
      const { edge, eaveCutType, slopes, isShedHalfEdge } = eaveEdge;
      const { startHeight: z1, endHeight: z2, heightOffset } = eaveEdge;
      const bracketPlacementEdge =
        eaveBracketPlacement === EaveBracketPlacement.START_TO_END ? polyLine2Copy(edge) : polyLine2ReverseCopy(edge);

      const propSrc = await getPropSrc(propCatalog, eaveBracketPropId, mergedPropLibrary);

      if (!propSrc) {
        continue;
      }

      const eaveBracketPropAttributes = eaveBracketPropId ? propCatalog[eaveBracketPropId] : undefined;
      // eaveBracketPropAttributes.viewNotes is undefined until someone configures a note on this catalog prop ,so merge with the default table rather than indexing it directly.
      const eaveBracketViewNotes = mergeViewNoteTables(defaultPropViewNotes, eaveBracketPropAttributes?.viewNotes);
      const [wallNormX, wallNormY] = polyLine2EdgeNormal(bracketPlacementEdge, 0);

      const radians =
        vec2AngleBetween(0, 1, -wallNormX, -wallNormY) +
        (eaveBracketPlacement === EaveBracketPlacement.START_TO_END ? 0 : Math.PI);

      let xoffset = (aabb3XLength(propSrc.boundingBox) * GLTF_ASSET_SCALE) / 2;
      let yoffset = 0;

      const eaveRail = polyLine3New([
        bracketPlacementEdge[0],
        bracketPlacementEdge[1],
        (eaveBracketPlacement === EaveBracketPlacement.START_TO_END ? z1 : z2) + heightOffset,
        bracketPlacementEdge[2],
        bracketPlacementEdge[3],
        (eaveBracketPlacement === EaveBracketPlacement.START_TO_END ? z2 : z1) + heightOffset,
      ]);
      const eaveRailLength = polyLine3EdgeLength(eaveRail, 0);
      const { slope: edgeSlope } = slopes[0] ?? { slope: 0 };
      let bracketZOffset = eaveCutType !== EaveCutType.SQUARED ? fasciaSize : fasciaSize * Math.cos(edgeSlope);

      if (isShedHalfEdge) {
        const { slope } = slopes[slopes.length - 1];
        bracketZOffset = -fasciaSize + xoffset * Math.tan(slope);
      }

      [xoffset, yoffset] = vec2Rotate(xoffset, yoffset, radians);

      if (eaveBracketParams) {
        if (eaveBracketParams.length > 0) {
          for (const param of eaveBracketParams) {
            const BRACKET_INSET_DISTANCE = eaveBracketParamsNormalized || param < 1 ? param * eaveRailLength : param;
            if (BRACKET_INSET_DISTANCE > eaveRailLength) {
              continue;
            }

            const [ax, ay, az] = polyLine3EvalEdge(eaveRail, 0, BRACKET_INSET_DISTANCE, false);
            if (eaveBracketPropId) {
              props.push(
                propNew({
                  room: null,
                  src: eaveBracketPropId,
                  x: ax - xoffset,
                  y: ay - yoffset,
                  z: az - bracketZOffset,
                  rotationDegrees: radianToDegree(radians),
                  catalogType: PropCatalogType.PROP_CATALOG,
                  isExterior: true,
                  labels: hardCodedAutoPlacedPropLabels, // We could consider making this configurable.
                  descriptor: viewEntityDescriptorNew(
                    uid(),
                    ViewEntityType.PROP,
                    eaveBracketViewNotes[PropViewNoteType.PROP],
                  ),
                }),
              );
            }
          }
        }
      }
    }
  }

  return {
    subRoof: {
      props,
    },
  };
}
